'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

interface NavItem {
  label: string;
  href: string;
}

const navItems: NavItem[] = [
  { label: 'Overview', href: '/' },
  { label: 'System Health', href: '/health' },
  { label: 'Application Logs', href: '/logs' },
  { label: 'Admin Audit', href: '/audit' },
  { label: 'Users', href: '/users' },
  { label: 'Organizations', href: '/organizations' },
  { label: 'Brands & Onboarding', href: '/brands' },
  { label: 'Credits & Wallets', href: '/credits' },
  { label: 'AI Jobs & Reconciliation', href: '/jobs' },
  { label: 'Campaigns & Publishing', href: '/campaigns' },
];

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, onClose }) => {
  const pathname = usePathname();

  const navContent = (
    <aside className="w-64 h-full border-r border-zinc-800 bg-zinc-950 flex flex-col justify-between shrink-0 select-none">
      <div className="flex flex-col flex-1 min-h-0">
        <div className="h-14 border-b border-zinc-800 flex items-center justify-between px-5">
          <div className="flex items-center gap-2.5">
            <div className="w-2.5 h-2.5 bg-emerald-500 rounded-sm" />
            <span className="font-semibold text-zinc-100 text-sm tracking-tight font-sans">
              iMarket Admin
            </span>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden text-zinc-400 hover:text-zinc-200 p-1 rounded"
              aria-label="Close navigation"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <div className="p-3 flex-1 overflow-y-auto">
          <div className="text-[10px] uppercase font-mono tracking-wider text-zinc-500 px-3 py-1.5 font-semibold">
            Operations & Control
          </div>

          <nav className="space-y-0.5 mt-1">
            {navItems.map((item) => {
              const isActive = pathname === item.href;

              return (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={onClose}
                  className={`flex items-center justify-between px-3 py-2 rounded text-xs font-mono transition-colors ${
                    isActive
                      ? 'bg-zinc-800 text-emerald-400 font-medium'
                      : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/80'
                  }`}
                >
                  <span>{item.label}</span>
                  {isActive && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="p-4 border-t border-zinc-800/80 text-[11px] font-mono text-zinc-500">
        <div className="flex items-center justify-between">
          <span>Security Model</span>
          <span className="text-emerald-500 text-[10px]">Active</span>
        </div>
        <div className="text-zinc-600 text-[10px] mt-0.5">Deny-by-default enforced</div>
      </div>
    </aside>
  );

  return (
    <>
      {/* Desktop static sidebar */}
      <div className="hidden lg:flex lg:shrink-0 h-screen sticky top-0">
        {navContent}
      </div>

      {/* Mobile/Tablet slide-over drawer */}
      {isOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex">
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity"
            onClick={onClose}
            aria-hidden="true"
          />
          <div className="relative z-10 flex">
            {navContent}
          </div>
        </div>
      )}
    </>
  );
};
